import React, { useMemo } from 'react';

import Mousetrap from 'mousetrap';

import { Box } from '@atlaskit/primitives/compiled';
import { MiniModalLoadingFallbackComponent } from '@atlassian/conversation-assistant/ui/mini-modal-fallback';

// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports
import type { MiniModalPropsInternal } from '../common/types/shared-types';

const LazyAIMiniModal = React.lazy(() =>
	import(/* webpackChunkName: "@atlaskit-internal_ai-mini-modal" */ './main').then((module) => ({
		default: module.AIMiniModal,
	})),
);

export const preloadAIMiniModal = () => {
	import(/* webpackChunkName: "@atlaskit-internal_ai-mini-modal" */ './main').then((module) => ({
		default: module.AIMiniModal,
	}));
};

export const AIMiniModal = (props: MiniModalPropsInternal): React.JSX.Element => {
	const mousetrapRef = React.useRef<Mousetrap.MousetrapInstance | null>(null);
	const setContainerRef = React.useCallback((node: any) => {
		mousetrapRef.current?.reset();
		if (!node) {
			return;
		}
		mousetrapRef.current = new Mousetrap(node);
	}, []);

	const suppressKeyPress: React.KeyboardEventHandler<HTMLDivElement> = (event) => {
		mousetrapRef.current?.bind(event.key.toLocaleLowerCase(), (e) => e.stopPropagation());
	};

	const miniModal = useMemo(() => {
		// 48px = nav header height, 8px = object sidebar bottom padding, 16px = AI smart button bottom margin, 8px = extra margin so it doesn't align with nav header
		const defaultTopOffset = 48 + 8 + 16 + 8;

		return (
			<React.Suspense
				fallback={
					<MiniModalLoadingFallbackComponent
						topOffset={props.topOffset || defaultTopOffset}
						isNav4Enabled={props.isNav4Enabled}
					/>
				}
			>
				<LazyAIMiniModal {...props} topOffset={props.topOffset || defaultTopOffset} />
			</React.Suspense>
		);
	}, [props]);

	return (
		<Box
			ref={setContainerRef}
			onKeyDown={suppressKeyPress}
			testId="ai-smart-button-mini-modal-wrapper"
		>
			{props.overrideMiniModalWrapper
				? props.overrideMiniModalWrapper(miniModal)
				: miniModal}
		</Box>
	);
};
